import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../../config/firebase';
import {
  doc, getDoc, setDoc, addDoc, updateDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { NuevoContratoModal } from '../MantenimientosView';
import FinalizarVisitaModal from './FinalizarVisitaModal';
import FichaClienteModal from './FichaClienteModal';
import FichaActivoModal from './FichaActivoModal';
import { reportClientError } from '../../utils/errorReporting';
import { tieneFotosDelEquipo } from '../../utils/tecnicoValidacion';
import { enqueue } from '../../services/offlineQueue';
import { trackEvent, ordenIdAnalytics } from '../../lib/analytics';

// Motivos de "He tenido que usar StelOrder" — sustituye la nota manual que
// iba a llevar Pablo en papel durante el piloto (Rafa, 2026-11-03).
const MOTIVOS_SALIDA_STELORDER = [
  ['cobro',               'Cobro'],
  ['material',            'Material'],
  ['garantia',            'Garantía'],
  ['cliente_no_encontrado', 'Cliente no encontrado'],
  ['otro',                'Otro'],
];

// StelOrder no trae un campo de "tipo de equipo" estructurado (a diferencia
// de marca/modelo) — se adivina del propio nombre del activo, que suele
// empezar por la categoría ("BOMBA DE CALOR ARISTON NUOS..."). Mismo
// criterio que detectarTipoEquipoPM() en FinalizarVisitaModal.js, pero en
// texto libre (no claves fijas) para precargar el campo editable de "Editar
// activo" en vez de dejarlo siempre en blanco.
const TIPOS_EQUIPO_CONOCIDOS = [
  'BOMBA DE CALOR', 'AIRE ACONDICIONADO', 'CONDUCTOS', 'AEROTERMIA',
  'CALDERA', 'TERMO ELECTRICO', 'CALENTADOR DE GAS', 'DESCALCIFICADOR',
];
function detectarTipoDelEquipo(equipmentInfo) {
  const texto = String(equipmentInfo || '').toUpperCase();
  return TIPOS_EQUIPO_CONOCIDOS.find(t => texto.includes(t)) || '';
}

// ── Checklist progress bar ────────────────────────────────────────────────────
function ChecklistProgress({ items }) {
  const total   = items.length;
  const checked = items.filter(i => i.checked).length;
  const pct     = total > 0 ? Math.round((checked / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${pct === 100 ? 'bg-emerald-500' : 'bg-emerald-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-xs font-bold flex-shrink-0 ${pct === 100 ? 'text-emerald-600' : 'text-gray-500'}`}>
        {checked}/{total}
      </span>
    </div>
  );
}

const STATE_LABEL = {
  651457:  'Pendiente',
  1018827: 'En ruta',
  1016222: 'Pend. material',
  1016221: 'Pend. sustitución',
  1007173: 'Pend. pago',
  651428:  'Cerrado',
  1016219: 'Cobrado',
  1004686: 'Garantía',
  1016220: 'Ausente',
  651446:  'Rechazado',
  1040205: 'Pend. presupuesto',
  1305009: 'Instalación',
  1069430: 'PM',
};
const STATE_COLOR = {
  651457:  'bg-yellow-100 text-yellow-700',
  1018827: 'bg-blue-100 text-blue-700',
  1016222: 'bg-orange-100 text-orange-700',
  1016221: 'bg-purple-100 text-purple-700',
  1007173: 'bg-amber-100 text-amber-700',
  651428:  'bg-green-100 text-green-700',
  1016219: 'bg-green-100 text-green-700',
  1004686: 'bg-teal-100 text-teal-700',
  1016220: 'bg-gray-100 text-gray-600',
  651446:  'bg-red-100 text-red-700',
  1040205: 'bg-indigo-100 text-indigo-700',
  1305009: 'bg-cyan-100 text-cyan-700',
  1069430: 'bg-rose-100 text-rose-700',
};

function formatPhone(raw) {
  if (!raw) return null;
  const clean = raw.replace(/[^0-9+]/g, '');
  if (clean.startsWith('+')) return clean.slice(1);
  if (clean.startsWith('00')) return clean.slice(2);
  if (clean.length === 9) return '34' + clean;
  return '34' + clean;
}

// Extrae la marca del equipo para indexar el feedback
function extractBrand(equipmentInfo) {
  if (!equipmentInfo) return null;
  const m = equipmentInfo.match(/(?:marca|brand)[:\s]+([^\s\-,]+)/i);
  if (m) return m[1].toLowerCase();
  const first = equipmentInfo.trim().split(/[\s\-,]/)[0];
  return first ? first.toLowerCase() : null;
}

function ListSection({ title, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">{title}</p>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
            <span className="flex-shrink-0 text-gray-400">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StepsList({ steps }) {
  if (!steps?.length) return null;
  return (
    <div>
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Pasos</p>
      <ol className="space-y-1.5">
        {steps.map((step, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 text-red-700 text-xs font-bold flex items-center justify-center mt-0.5">
              {i + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

// Burbuja de chat individual
function ChatBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex items-end gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {!isUser && (
        <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mb-0.5">
          <svg className="w-3 h-3 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
      )}
      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
        isUser
          ? 'bg-violet-600 text-white rounded-br-sm'
          : 'bg-white border border-violet-100 text-gray-800 rounded-bl-sm shadow-sm'
      }`}>
        {msg.text}
      </div>
    </div>
  );
}

export default function TechnicianOrderDetailView({ order, onBack }) {
  const { user } = useAuth();

  // Estado local de la orden (se actualiza tras finalizar sin recargar toda la lista)
  const [localOrder, setLocalOrder]       = useState(order);
  const [showFinalizarModal, setShowFinalizarModal] = useState(false);

  // Edición tarjeta cliente
  const [editingClient,    setEditingClient]    = useState(false);
  const [editClientName,   setEditClientName]   = useState('');
  const [editClientPhone,  setEditClientPhone]  = useState('');
  const [savingClient,     setSavingClient]     = useState(false);

  // Edición tarjeta activo
  const [editingActivo,    setEditingActivo]    = useState(false);
  const [editActivoMarca,  setEditActivoMarca]  = useState('');
  const [editActivoModelo, setEditActivoModelo] = useState('');
  const [editActivoSerie,  setEditActivoSerie]  = useState('');
  const [editActivoTipo,   setEditActivoTipo]   = useState('');
  const [editActivoFechaGarantia, setEditActivoFechaGarantia] = useState(''); // YYYY-MM-DD
  const [errorActivo,      setErrorActivo]      = useState('');
  const [savingActivo,     setSavingActivo]     = useState(false);

  // Estado del análisis
  const [analysis, setAnalysis]   = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiError, setAiError]     = useState('');
  const [threadId, setThreadId]   = useState(null);
  const [usedContext, setUsedContext] = useState(0); // nº de casos de aprendizaje usados

  // Estado del chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput]       = useState('');
  const [chatLoading, setChatLoading]   = useState(false);
  const chatBottomRef = useRef(null);

  // Estado del checklist (pre-rellenado con la avería reportada)
  const [checklist, setChecklist]             = useState(null);
  const [checklistDescription, setChecklistDescription] = useState(order['private-comments'] || '');
  const [generatingChecklist, setGeneratingChecklist]   = useState(false);
  const [checklistError, setChecklistError]   = useState('');

  // Estado del feedback
  const [feedbackRating, setFeedbackRating] = useState(null); // 'up' | 'down'
  const [feedbackText, setFeedbackText]     = useState('');
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackSaved, setFeedbackSaved]   = useState(false);
  const [feedbackError, setFeedbackError]   = useState('');

  // Contrato de mantenimiento (solo PM 1069430)
  const [showContratoModal, setShowContratoModal] = useState(false);
  const [showFichaCliente, setShowFichaCliente] = useState(false);
  const [showFichaActivo,  setShowFichaActivo]  = useState(false);

  // "He tenido que usar StelOrder" — ver MOTIVOS_SALIDA_STELORDER arriba.
  const [showStelOrderModal, setShowStelOrderModal] = useState(false);
  const [stelOrderMotivo,    setStelOrderMotivo]     = useState('');
  const [stelOrderDetalle,   setStelOrderDetalle]    = useState('');

  // Notas IA de la ruta (Parte 1 del plan de notas IA, 2026-10-05) — solo
  // lectura, las genera oficina al imprimir la hoja (ver generarNotaRutaIA.js
  // y RouteGenerator.js); aquí solo se muestran si ya existen para hoy.
  const [notaIA, setNotaIA] = useState(null);
  const [notaIALoading, setNotaIALoading] = useState(true);

  const [startingVisit, setStartingVisit] = useState(false);
  const [iniciarError,  setIniciarError]  = useState('');

  const orderId       = String(localOrder.id);
  const stateId       = localOrder['document-state-id'];
  // Usado también más abajo, para el botón sticky de Finalizar visita — una
  // orden ya cerrada no necesita restaurar su sesión de IA/checklist al
  // abrirla (ver el useEffect de abajo), solo la que sigue accionable.
  // 1007173 (Pendiente de pago) se sacó de aquí: ya no es un estado que el
  // técnico "retome" — es un estado final (el trabajo está hecho, solo
  // falta cobrar la factura) y reclamar ese cobro es tarea de oficina, no
  // una visita de campo (decisión de Rafa, 2026-10-15). Ver también
  // getBaseOutcomes() en FinalizarVisitaModal.js, que ya no tiene una rama
  // específica para este estado.
  // 1069430 (Puesta en marcha): faltaba aquí — la orden solo mostraba el botón
  // "Crear contrato de mantenimiento" (ver bloque PM más abajo), pero nunca el
  // de Finalizar visita, así que el resultado 'puesta_marcha' de
  // FinalizarVisitaModal.js (getBaseOutcomes/OUTCOMES_PM) era inalcanzable
  // desde la UI pese a existir en el código.
  const showStickyButton = stateId === 1018827 || stateId === 1004686 || stateId === 1305009 || stateId === 1069430;
  const esPuestaMarcha   = stateId === 1069430;
  // "Pendiente" (651457): el técnico todavía no ha iniciado esta visita —
  // antes esto era un callejón sin salida en la app (solo un mensaje
  // informativo); ahora "Iniciar visita" la mueve a "En ruta" para poder
  // finalizarla después. Piloto desde el 13/10/2026.
  const showIniciarButton = stateId === 651457;
  const clientName    = localOrder.clientName || 'Sin nombre';
  const phone         = localOrder.clientPhone || '';
  const address       = localOrder.addressText || '';
  const equipmentInfo = localOrder.equipmentInfo || '';
  const mapsUrl       = localOrder.latitude && localOrder.longitude
    ? `https://maps.google.com/?q=${localOrder.latitude},${localOrder.longitude}`
    : `https://maps.google.com/?q=${encodeURIComponent(address)}`;

  const whatsappNumber = formatPhone(phone);
  const whatsappMsg = encodeURIComponent(
    `¡Hola ${clientName}! 👋\n\n` +
    `Te contacto desde Climatización Zapata respecto a la orden de trabajo: ${order['full-reference'] || order.id}\n\n` +
    (equipmentInfo ? `📱 *Equipo:* ${equipmentInfo}\n\n` : '') +
    `Te informamos que el técnico está en camino para realizar su orden de trabajo.\n\n` +
    `Si necesita realizar algún cambio o tiene alguna duda contacte con nuestra oficina de atención al cliente en el 966766306.\n\n` +
    `¡Gracias por confiar en nosotros! 🛠️`
  );

  // Restaurar sesión IA y checklist al abrir la orden — solo si sigue
  // accionable (en ruta/garantía/instalación) o todavía sin iniciar
  // (pendiente): el técnico puede generar el diagnóstico antes de pulsar
  // "Iniciar visita", y no debe perderse al volver a entrar. Antes se hacía
  // siempre, en cada apertura de CUALQUIER orden, aunque el técnico solo
  // estuviera consultando una ya cerrada hace días.
  useEffect(() => {
    if (!showStickyButton && !showIniciarButton) return;
    const restoreSession = async () => {
      try {
        const [aiSnap, clSnap] = await Promise.all([
          getDoc(doc(db, 'ai_sessions', orderId)),
          getDoc(doc(db, 'checklist_completions', orderId)),
        ]);
        if (aiSnap.exists()) {
          const data = aiSnap.data();
          setAnalysis(data.analysis || null);
          setThreadId(data.threadId || null);
          setChatMessages(data.chatMessages || []);
          setFeedbackSaved(data.feedbackSaved || false);
        }
        if (clSnap.exists()) {
          setChecklist(clSnap.data());
        }
      } catch { /* sin sesión previa, arrancar limpio */ }
    };
    restoreSession();
  }, [orderId, showStickyButton, showIniciarButton]);

  // Auto-scroll al final del chat cuando llegan mensajes nuevos
  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  // Notas IA de la ruta — solo lectura, no se regeneran desde aquí.
  useEffect(() => {
    const fecha = localOrder.date ? String(localOrder.date).slice(0, 10) : null;
    if (!fecha) { setNotaIALoading(false); return; }
    setNotaIALoading(true);
    fetch(`/.netlify/functions/getNotaRutaIA?ordenId=${orderId}&fecha=${fecha}`, { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(data => { if (data.success) setNotaIA(data.nota); })
      .catch(() => {})
      .finally(() => setNotaIALoading(false));
  }, [orderId, localOrder.date]);

  // ── Checklist generado por IA ─────────────────────────────────────────────────
  const handleGenerateChecklist = useCallback(async () => {
    if (!checklistDescription.trim() || generatingChecklist) return;
    setGeneratingChecklist(true);
    setChecklistError('');
    try {
      const resp = await fetch('/.netlify/functions/generateChecklist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          reference:          order['full-reference'] || String(order.id),
          equipmentInfo,
          problemDescription: order['private-comments'] || '',
          addendum:           order.addendum || '',
          techDescription:    checklistDescription.trim(),
        }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error);

      const completion = {
        items:          data.items.map(i => ({ ...i, checked: false })),
        techDescription: checklistDescription.trim(),
        orderId,
        technicianId:   user?.uid  || null,
        technicianName: user?.name || null,
        generatedAt:    serverTimestamp(),
        completedAt:    null,
      };
      await setDoc(doc(db, 'checklist_completions', orderId), completion);
      setChecklist(completion);
      trackEvent('checklist_ia_generado', { orden_id: ordenIdAnalytics(order), numItems: completion.items.length });
    } catch (e) {
      setChecklistError(e.message || 'Error al generar el checklist');
    } finally {
      setGeneratingChecklist(false);
    }
  }, [checklistDescription, generatingChecklist, order, orderId, equipmentInfo, user]);

  const handleToggleItem = useCallback(async (itemId) => {
    if (!checklist) return;
    const newItems = checklist.items.map(i =>
      i.id === itemId ? { ...i, checked: !i.checked } : i
    );
    const allDone = newItems.every(i => !i.required || i.checked);
    const updated = {
      ...checklist,
      items: newItems,
      completedAt: allDone ? new Date().toISOString() : null,
    };
    setChecklist(updated);
    try {
      await setDoc(doc(db, 'checklist_completions', orderId),
        { items: newItems, completedAt: allDone ? serverTimestamp() : null },
        { merge: true }
      );
    } catch { /* fallo silencioso */ }
  }, [checklist, orderId]);

  // ── Generar análisis inicial ─────────────────────────────────────────────────
  const handleAnalyze = useCallback(async () => {
    setAnalyzing(true);
    setAiError('');
    try {
      // La búsqueda de casos similares ya no la hace el frontend: la tool
      // consultar_casos_similares del backend la sustituye (se fuerza en el
      // primer turno de analyzeWorkorder) y devuelve cuántos casos usó.
      const resp = await fetch('/.netlify/functions/analyzeWorkorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          reference:          order['full-reference'] || String(order.id),
          equipmentInfo,
          problemDescription: order['private-comments'] || '',
          addendum:           order.addendum || '',
        }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error);
      setUsedContext(data.casosSimilaresUsados || 0);

      // Guardar sesión en Firestore (threadId + análisis)
      await setDoc(doc(db, 'ai_sessions', orderId), {
        threadId:     data.threadId,
        equipmentInfo,
        analysis:     data.summary,
        chatMessages: [],
        feedbackSaved: false,
        createdAt:    serverTimestamp(),
      });

      setThreadId(data.threadId);
      setAnalysis(data.summary);
      setChatMessages([]);
      setFeedbackRating(null);
      setFeedbackText('');
      setFeedbackSaved(false);
    } catch (e) {
      setAiError(e.message || 'Error al generar el análisis');
    } finally {
      setAnalyzing(false);
    }
  }, [order, orderId, equipmentInfo]);

  // ── Enviar mensaje de chat ───────────────────────────────────────────────────
  const handleSendMessage = useCallback(async () => {
    const text = chatInput.trim();
    if (!text || !threadId || chatLoading) return;

    const userMsg    = { role: 'user', text };
    const withUser   = [...chatMessages, userMsg];
    setChatMessages(withUser);
    setChatInput('');
    setChatLoading(true);

    try {
      const resp = await fetch('/.netlify/functions/chatWithAssistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ threadId, message: text }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error);

      // Capturar threadId si se creó uno nuevo tras un reset
      if (data.threadId && data.threadId !== threadId) setThreadId(data.threadId);

      const withReply = [...withUser, { role: 'assistant', text: data.response }];
      setChatMessages(withReply);

      // Persistir historial de chat en Firestore
      await setDoc(doc(db, 'ai_sessions', orderId), { chatMessages: withReply }, { merge: true });
    } catch (e) {
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', text: `⚠️ Error al conectar: ${e.message}` },
      ]);
    } finally {
      setChatLoading(false);
    }
  }, [chatInput, threadId, chatLoading, chatMessages, orderId]);

  // ── Reset del hilo de chat (evita acumulación de contexto costosa) ──────────
  const handleResetChat = useCallback(() => {
    setThreadId(null);
    setChatMessages([]);
  }, []);

  // ── Guardar feedback del técnico ─────────────────────────────────────────────
  const handleSaveFeedback = useCallback(async () => {
    if (feedbackRating === null || feedbackSaving || feedbackSaved) return;
    setFeedbackSaving(true);
    try {
      await addDoc(collection(db, 'ai_feedback'), {
        orderId,
        equipmentInfo,
        equipmentBrand:  extractBrand(equipmentInfo),
        aiDiagnosis:     analysis?.solution || '',
        actualDiagnosis: feedbackText.trim(),
        helpful:         feedbackRating === 'up',
        technicianId:    user?.uid   || null,
        technicianName:  user?.name  || null,
        timestamp:       serverTimestamp(),
      });
      // Marcar en sesión para no pedir feedback dos veces
      await setDoc(doc(db, 'ai_sessions', orderId), { feedbackSaved: true }, { merge: true });
      setFeedbackSaved(true);
    } catch (e) {
      // Antes fallaba en silencio — para el técnico era indistinguible de un
      // éxito. Ahora se avisa y queda registrado.
      reportClientError(e, { source: 'silent-catch', context: `guardar feedback IA · orden ${orderId}` });
      setFeedbackError('No se pudo guardar tu valoración. Inténtalo de nuevo.');
    } finally {
      setFeedbackSaving(false);
    }
  }, [feedbackRating, feedbackSaving, feedbackSaved, orderId, equipmentInfo, analysis, feedbackText, user]);

  // ── Guardar datos del cliente ────────────────────────────────────────────────
  const handleSaveClient = useCallback(async () => {
    setSavingClient(true);
    try {
      const fbId     = localOrder._firebaseId || String(localOrder.id);
      const clientId = localOrder.clienteId;
      const nombre   = editClientName.trim();
      const telefono = editClientPhone.trim();

      const writes = [
        // datos denormalizados en la orden — clientePhone (no clienteTelefono)
        // es el nombre de campo que usa el resto de la app para esta
        // colección (OrdenesView, syncOrdenes.js); con el otro nombre este
        // guardado quedaba huérfano, invisible para cualquier otra vista.
        updateDoc(doc(db, 'ordenes', fbId), {
          clienteNombre: nombre,
          clientePhone:  telefono,
        }),
      ];

      // registro real del cliente — pasa por saveCliente.js (requiere sesión)
      // en vez de escribir Firestore directo.
      if (clientId) {
        writes.push(
          fetch('/.netlify/functions/saveCliente', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
            body: JSON.stringify({ id: String(clientId), ordenId: orderId, nombre, movil: telefono }),
          }).then(r => r.json()).then(d => { if (!d.success) throw new Error(d.error || 'Error guardando cliente'); })
        );
      }

      await Promise.all(writes);

      setLocalOrder(prev => ({
        ...prev,
        clientName:  nombre,
        clientPhone: telefono,
      }));
      setEditingClient(false);
    } catch (e) {
      console.error('Error guardando cliente:', e.message);
    } finally {
      setSavingClient(false);
    }
  }, [editClientName, editClientPhone, localOrder]);

  // ── Guardar datos del activo ─────────────────────────────────────────────────
  const handleSaveActivo = useCallback(async () => {
    const activoId = localOrder.activoId || localOrder.activoIds?.[0];
    if (!activoId) return;
    setSavingActivo(true);
    setErrorActivo('');
    try {
      const resp = await fetch('/.netlify/functions/saveActivo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          id:          activoId,
          ordenId:     orderId,
          marca:       editActivoMarca.trim(),
          modelo:      editActivoModelo.trim(),
          numeroSerie: editActivoSerie.trim() || null,
          tipoEquipo:  editActivoTipo.trim()  || null,
          fechaInicioGarantia: editActivoFechaGarantia || null,
        }),
      });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error || 'Error guardando activo');
      const newEquipmentInfo = [editActivoMarca.trim(), editActivoModelo.trim()].filter(Boolean).join(' ');
      setLocalOrder(prev => ({
        ...prev,
        equipmentInfo:     newEquipmentInfo,
        marcaActivo:       editActivoMarca.trim(),
        modeloActivo:      editActivoModelo.trim(),
        assetBrand:        editActivoMarca.trim(),
        assetModel:        editActivoModelo.trim(),
        assetSerialNumber: editActivoSerie.trim(),
        tipoEquipo:        editActivoTipo.trim(),
        ...(editActivoFechaGarantia ? { assetWarrantyStartDate: `${editActivoFechaGarantia}T00:00:00Z` } : {}),
      }));
      setEditingActivo(false);
    } catch (e) {
      // Antes solo iba a la consola: el técnico creía haberlo guardado y el
      // cierre de ruta le seguía bloqueando por el mismo dato.
      setErrorActivo(e.message || 'No se pudo guardar el activo');
    } finally {
      setSavingActivo(false);
    }
  }, [editActivoMarca, editActivoModelo, editActivoSerie, editActivoTipo, editActivoFechaGarantia, localOrder]);

  // ── Iniciar visita: Pendiente → En ruta ──────────────────────────────────────
  // Antes esta app no tenía forma de mover una orden "Pendiente" — el técnico
  // dependía de la app antigua de StelOrder (o de que oficina la creara ya en
  // "En ruta") para poder empezarla. Reutiliza updateWorkorderState.js, el
  // mismo endpoint que ya usa el cierre de ruta para los cambios automáticos
  // de estado — la comprobación de propiedad (IDOR) y la cola de
  // sincronización con StelOrder ya están cubiertas ahí.
  // Sin cola offline (a diferencia de finalizarVisita/createRouteClosure) —
  // en mala cobertura fallaba sin reintento, justo el escenario real de un
  // técnico en campo. Mismo patrón de detección de error de red + enqueue
  // que ya usa handleEnviarJustificacion en TechnicianOrdersView.js.
  const handleIniciarVisita = useCallback(async () => {
    setStartingVisit(true);
    setIniciarError('');
    const url = '/.netlify/functions/updateWorkorderState';
    const payload = { workorderId: localOrder.id, newStateId: 1018827 };
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        enqueue({ kind: 'iniciarVisita', url, body: payload });
        setLocalOrder(prev => ({ ...prev, 'document-state-id': 1018827 }));
        return;
      }
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error al iniciar la visita');
      setLocalOrder(prev => ({ ...prev, 'document-state-id': 1018827 }));
    } catch (e) {
      if (e instanceof TypeError || /fetch|network|Failed to fetch|conexión/i.test(e.message || '')) {
        enqueue({ kind: 'iniciarVisita', url, body: payload });
        setLocalOrder(prev => ({ ...prev, 'document-state-id': 1018827 }));
      } else {
        setIniciarError(e.message || 'No se pudo iniciar la visita');
      }
    } finally {
      setStartingVisit(false);
    }
  }, [localOrder.id]);

  // ── Finalizar visita: éxito ──────────────────────────────────────────────────
  const handleFinalizarSuccess = useCallback((result) => {
    setShowFinalizarModal(false);
    const patch = {
      'document-state-id':  result.newStateId,
      signed:               result.signed,
      'total-amount':       result.totalAmount ?? localOrder['total-amount'],
      'payment-option-id':  result['payment-option-id'] ?? localOrder['payment-option-id'],
      observaciones:        result.observaciones ?? localOrder.observaciones,
    };
    setLocalOrder(prev => ({ ...prev, ...patch }));
    if (result.queued) {
      alert('Visita guardada sin conexión. Se enviará automáticamente en cuanto vuelvas a tener cobertura.');
    } else if (result.stelSyncFailed) {
      alert('Visita guardada. Aviso: no se pudo sincronizar con StelOrder. Se sincronizará automáticamente más tarde.');
    }
    // Sin conexión, la visita queda en la cola offline — todavía no existe de
    // verdad en el servidor. Forzar una recarga de la lista en ese momento
    // solo consigue un error de red que contradice el aviso de éxito que
    // acabamos de mostrar, y la orden vuelve a verse como si no se hubiera
    // tocado. En su lugar, se aplica el mismo parche optimista directamente
    // sobre la lista ya cargada; la recarga real de servidor solo tiene
    // sentido cuando la visita ya se guardó de verdad.
    onBack(result.queued ? { patch: { id: orderId, ...patch } } : { refresh: true });
  }, [onBack, orderId, localOrder]);

  // Corregir una visita ya finalizada — antes esto exigía llamar a oficina.
  // Se permite reabrir FinalizarVisitaModal (precargado con lo ya guardado)
  // durante una ventana corta tras cerrarla, para un error de transcripción
  // detectado enseguida (línea mal puesta, DNI mal escrito...); pasada esa
  // ventana, sigue siendo cosa de oficina — evita corregir a ciegas algo que
  // ya pudo haberse facturado.
  const RESULTADOS_TECNICO = new Set([
    'cobrada', 'cobrada_sin_firma', 'pendiente_pago', 'ausente',
    'pendiente_material', 'pendiente_presupuesto', 'pendiente_sustitucion',
    'puesta_marcha',
  ]);
  const esResultadoTecnico = typeof localOrder.estado === 'string' &&
    (RESULTADOS_TECNICO.has(localOrder.estado) || localOrder.estado.startsWith('garantia_'));
  const VENTANA_CORRECCION_MS = 6 * 60 * 60 * 1000; // 6 horas — el turno del día
  const dentroDeVentana = !!localOrder.syncedAt &&
    (Date.now() - new Date(localOrder.syncedAt).getTime()) < VENTANA_CORRECCION_MS;
  const showCorregirButton = !showStickyButton && esResultadoTecnico && dentroDeVentana;

  // "He tenido que usar StelOrder" — solo un evento para PostHog, no toca
  // nada del servidor; sustituye la nota en papel que iba a llevar Pablo.
  const handleEnviarStelOrder = () => {
    if (!stelOrderMotivo) return;
    if (stelOrderMotivo === 'otro' && !stelOrderDetalle.trim()) return;
    trackEvent('salida_a_stelorder', {
      orden_id: ordenIdAnalytics(localOrder),
      motivo: stelOrderMotivo,
      detalle: stelOrderDetalle.trim().slice(0, 300) || undefined,
      pantallaOrigen: 'tecnico:orden_detalle',
    });
    setShowStelOrderModal(false);
    setStelOrderMotivo('');
    setStelOrderDetalle('');
  };

  return (
    <>
    <div className={`p-4 space-y-4 ${(showStickyButton || showCorregirButton || showIniciarButton) ? 'pb-40' : 'pb-10'}`}>

      {/* ── Top bar ── */}
      <div className="flex items-center gap-3">
        <button data-attr="orden-volver"
          onClick={() => {
            // "Iniciar visita" solo actualiza localOrder en este componente —
            // sin esto, al volver la lista seguía mostrando "Pendiente" hasta
            // un refresco manual, y el técnico podía pensar que no funcionó
            // y volver a pulsar el botón.
            // Lo mismo con los datos del activo corregidos en "Editar activo":
            // el cierre de ruta los valida (reglas Ariston) desde la lista.
            const patch = {};
            if (localOrder['document-state-id'] !== order['document-state-id'])
              patch['document-state-id'] = localOrder['document-state-id'];
            for (const k of ['assetBrand', 'assetModel', 'assetSerialNumber', 'assetWarrantyStartDate'])
              if (localOrder[k] !== order[k]) patch[k] = localOrder[k];
            onBack(Object.keys(patch).length ? { patch: { id: order.id, ...patch }, kind: 'iniciarVisita' } : false);
          }}
          className="p-2 -ml-2 text-gray-400 hover:text-gray-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="font-bold text-gray-900 text-lg flex-1">{order['full-reference'] || `#${order.id}`}</h2>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${STATE_COLOR[stateId] || 'bg-gray-100 text-gray-600'}`}>
          {STATE_LABEL[stateId] || 'Desconocido'}
        </span>
      </div>

      {/* ── He tenido que usar StelOrder (piloto) ── */}
      <button data-attr="orden-salida-stelorder"
        onClick={() => setShowStelOrderModal(true)}
        className="w-full py-1.5 text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors"
      >
        He tenido que usar StelOrder
      </button>

      {/* ── Notas IA de la ruta (las genera oficina al imprimir, solo lectura) ── */}
      {!notaIALoading && notaIA && (
        notaIA.diagnostico?.length || notaIA.riesgos?.length || notaIA.materialSugerido?.length || notaIA.oportunidadComercial
      ) && (
        <div className="ph-no-capture bg-violet-50 border border-violet-200 rounded-2xl p-4 space-y-2">
          <p className="text-xs font-bold text-violet-700 uppercase tracking-wider">🔮 Notas IA</p>
          {notaIA.diagnostico?.map((d, i) => (
            <p key={i} className="text-sm text-gray-700">{d.texto}</p>
          ))}
          {notaIA.riesgos?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {notaIA.riesgos.map((r, i) => (
                <span key={i} className="text-xs font-semibold bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">⚠ {r}</span>
              ))}
            </div>
          )}
          {notaIA.materialSugerido?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {notaIA.materialSugerido.map((m, i) => (
                <span key={i} className={`text-xs font-semibold rounded-full px-2 py-0.5 ${m.disponible ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} ${m.prioridad === 'por_si_acaso' ? 'opacity-60' : ''}`}>
                  🔧 {m.nombre} ({m.disponible ? '✓ en stock' : m.encontradoEnCatalogo ? 'sin stock' : 'no en catálogo'}{m.codigoDespiece ? ` · despiece: ${m.codigoDespiece}` : ' · sin despiece'}{m.prioridad === 'por_si_acaso' ? ' · por si acaso' : ''})
                </span>
              ))}
            </div>
          )}
          {notaIA.oportunidadComercial && <p className="text-xs text-violet-700">💡 {notaIA.oportunidadComercial}</p>}
          <p className="text-[9px] text-gray-400">Material orientativo, no reservado — generado por IA, no sustituye tu criterio</p>
        </div>
      )}

      {/* ── Cliente ── */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border space-y-2.5">
        <div className="flex items-center justify-between gap-1.5">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Cliente</p>
          <div className="flex items-center gap-1.5">
            {!editingClient && localOrder.clienteId && (
              <button data-attr="cliente-ver-ficha"
                onClick={() => setShowFichaCliente(true)}
                className="text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5"
              >
                Ver ficha completa
              </button>
            )}
            {!editingClient && (
              <button data-attr="cliente-editar"
                onClick={() => { setEditClientName(clientName); setEditClientPhone(phone); setEditingClient(true); }}
                className="p-1 text-gray-300 hover:text-gray-500 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {editingClient ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs text-gray-400">Nombre</label>
              <input type="text" value={editClientName} onChange={e => setEditClientName(e.target.value)}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-200" autoFocus />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-400">Teléfono</label>
              <input type="tel" value={editClientPhone} onChange={e => setEditClientPhone(e.target.value)}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-200" />
            </div>
            <div className="flex gap-2">
              <button data-attr="cliente-editar-cancelar" onClick={() => setEditingClient(false)} className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-500">Cancelar</button>
              <button data-attr="cliente-editar-guardar" onClick={handleSaveClient} disabled={savingClient}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50">
                {savingClient ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="ph-no-capture font-semibold text-gray-900">{clientName}</p>
            {phone && (
              <div className="ph-no-capture flex items-center gap-2">
                <a data-attr="cliente-llamar" onClick={() => trackEvent('cliente_contactado', { orden_id: ordenIdAnalytics(localOrder), via: 'llamada' })} href={`tel:${phone}`} className="flex items-center gap-2 text-sm text-red-600 font-medium flex-1">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 8V5z" />
                  </svg>
                  {phone}
                </a>
                {whatsappNumber && (
                  <a data-attr="cliente-whatsapp" onClick={() => trackEvent('cliente_contactado', { orden_id: ordenIdAnalytics(localOrder), via: 'whatsapp' })}
                    href={`https://wa.me/${whatsappNumber}?text=${whatsappMsg}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-xl text-xs font-semibold"
                  >
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </a>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Dirección ── */}
      {address && (
        <div className="ph-no-capture bg-white rounded-2xl p-4 shadow-sm border space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Dirección</p>
          <p className="text-sm text-gray-700">{address}</p>
          <a data-attr="direccion-abrir-maps" onClick={() => trackEvent('cliente_contactado', { orden_id: ordenIdAnalytics(localOrder), via: 'maps' })} href={mapsUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Abrir en Google Maps
          </a>
        </div>
      )}

      {/* ── Activo ── */}
      {(equipmentInfo || localOrder.activoId || localOrder.activoIds?.[0]) && (
        <div className="bg-white rounded-2xl p-4 shadow-sm border space-y-3">

          {/* Cabecera */}
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Activo</p>
            <div className="flex items-center gap-1.5 flex-wrap justify-end">
              {!editingActivo && localOrder['work-order-type']?.name && (
                <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-0.5">
                  {localOrder['work-order-type'].name}
                </span>
              )}
              {/* Antes solo miraba assetHasImages (StelOrder) — contradecía
                  en la misma pantalla a "Fotos de la visita" más abajo, que
                  sí muestra las que el técnico acaba de subir aquí mismo:
                  un técnico que fotografiaba el equipo veía "Sin fotos"
                  arriba y sus propias fotos justo debajo. */}
              {!editingActivo && (
                tieneFotosDelEquipo(localOrder) ? (
                  <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
                    📷 Con fotos
                  </span>
                ) : (
                  <span className="text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5">
                    Sin fotos
                  </span>
                )
              )}
              {!editingActivo && (localOrder.activoId || localOrder.activoIds?.[0]) && (
                <button data-attr="activo-ver-ficha"
                  onClick={() => setShowFichaActivo(true)}
                  className="text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5"
                >
                  Ver ficha completa
                </button>
              )}
              {!editingActivo && (localOrder.activoId || localOrder.activoIds?.[0]) && (
                <button data-attr="activo-editar"
                  onClick={() => {
                    // marcaActivo/modeloActivo/tipoEquipo solo existían tras
                    // una edición previa — antes de la primera vez salían en
                    // blanco aunque StelOrder ya tuviera marca/modelo, así
                    // que había que volver a escribirlos en vez de solo
                    // corregirlos (al revés que el nº de serie, que sí viene
                    // precargado de StelOrder). Ahora caen a assetBrand/
                    // assetModel (StelOrder) y a una detección por el nombre
                    // del equipo para el tipo (Rafa, 2026-10-16).
                    setEditActivoMarca(localOrder.marcaActivo || localOrder.assetBrand || '');
                    setEditActivoModelo(localOrder.modeloActivo || localOrder.assetModel || '');
                    setEditActivoSerie(localOrder.assetSerialNumber || '');
                    setEditActivoTipo(localOrder.tipoEquipo || detectarTipoDelEquipo(localOrder.equipmentInfo) || '');
                    setEditActivoFechaGarantia((localOrder.assetWarrantyStartDate || '').split('T')[0]);
                    setErrorActivo('');
                    setEditingActivo(true);
                  }}
                  className="p-1 text-gray-300 hover:text-gray-500 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {editingActivo ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Marca</label>
                  <input type="text" value={editActivoMarca} onChange={e => setEditActivoMarca(e.target.value)}
                    className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200" autoFocus />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Tipo</label>
                  <input type="text" value={editActivoTipo} onChange={e => setEditActivoTipo(e.target.value)}
                    placeholder="BOMBA DE CALOR..."
                    className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200" />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-gray-400">Modelo</label>
                <input type="text" value={editActivoModelo} onChange={e => setEditActivoModelo(e.target.value)}
                  className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-gray-400">Nº de serie</label>
                <input type="text" value={editActivoSerie} onChange={e => setEditActivoSerie(e.target.value)}
                  className="w-full text-sm font-mono bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
              {/* Ariston la exige para liquidar la garantía (FECHA_PUESTA_MARCHA)
                  — sin ella el cierre de ruta bloquea la orden. Suele venir en
                  la factura de compra o en la pegatina de instalación. */}
              <div className="space-y-1">
                <label className="text-xs text-gray-400">Fecha inicio garantía (instalación)</label>
                <input type="date" value={editActivoFechaGarantia} onChange={e => setEditActivoFechaGarantia(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
              {errorActivo && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{errorActivo}</p>
              )}
              <div className="flex gap-2">
                <button data-attr="activo-editar-cancelar" onClick={() => setEditingActivo(false)} className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-500">Cancelar</button>
                <button data-attr="activo-editar-guardar" onClick={handleSaveActivo} disabled={savingActivo}
                  className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50">
                  {savingActivo ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Nombre e icono */}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm leading-snug">{equipmentInfo}</p>
                  {(localOrder.tipoEquipo || localOrder.tipo) && (
                    <p className="text-xs text-gray-400 mt-0.5">{localOrder.tipoEquipo || localOrder.tipo}</p>
                  )}
                </div>
              </div>

              {/* Número de serie */}
              {(localOrder.assetSerialNumber || localOrder.numeroSerie || localOrder.numSerie) && (
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5">
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider leading-none">Nº Serie</p>
                    <p className="text-sm font-mono text-gray-800 leading-tight mt-0.5">
                      {localOrder.assetSerialNumber || localOrder.numeroSerie || localOrder.numSerie}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

        </div>
      )}

      {/* ── Fotos de la visita ── */}
      {Array.isArray(localOrder.fotos) && localOrder.fotos.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm border">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Fotos de la visita</p>
          <div className="flex gap-2 overflow-x-auto">
            {localOrder.fotos.map((url, i) => (
              <a data-attr="foto-visita-abrir" key={url} href={url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                <img src={url} alt={`foto ${i + 1}`} className="w-24 h-24 object-cover rounded-lg border border-gray-200" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* ── Avería reportada ── */}
      {order['private-comments'] && (
        <div className="ph-no-capture bg-white rounded-2xl p-4 shadow-sm border">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Avería reportada</p>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{order['private-comments']}</p>
        </div>
      )}

      {/* ── Comentarios ── */}
      {order.comments && (
        <div className="ph-no-capture bg-white rounded-2xl p-4 shadow-sm border">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Comentarios</p>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{order.comments}</p>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          CONTRATO DE MANTENIMIENTO (solo PM)
      ══════════════════════════════════════════════════════ */}
      {stateId === 1069430 && (
        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-4 border border-red-200 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">📋</span>
            <p className="font-bold text-red-900 text-sm">Contrato de mantenimiento</p>
          </div>
          <p className="text-xs text-red-700 leading-relaxed">
            Es una puesta en marcha. Ofrece al cliente un contrato de mantenimiento anual y genera el enlace de firma ahora — el cliente lo completará desde su móvil.
          </p>
          <button data-attr="contrato-crear" onClick={() => setShowContratoModal(true)}
            className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm transition-colors">
            Crear contrato de mantenimiento
          </button>
        </div>
      )}

      {showFichaCliente && (
        <FichaClienteModal clienteId={localOrder.clienteId} ordenId={orderId} onClose={() => setShowFichaCliente(false)} />
      )}

      {showFichaActivo && (
        <FichaActivoModal
          activoId={localOrder.activoId || localOrder.activoIds?.[0]}
          ordenId={orderId}
          clienteId={localOrder.clienteId}
          onClose={() => setShowFichaActivo(false)}
        />
      )}

      {/* Modal "He tenido que usar StelOrder" (piloto) */}
      {showStelOrderModal && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/50">
          <div className="bg-white w-full rounded-t-3xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto" />
            <h3 className="font-bold text-gray-900 text-lg">He tenido que usar StelOrder</h3>
            <p className="text-sm text-gray-500">Solo queda anotado para mejorar la app — no cambia nada de esta orden.</p>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Motivo</label>
              <div className="grid grid-cols-2 gap-2">
                {MOTIVOS_SALIDA_STELORDER.map(([v, l]) => (
                  <button data-attr={`stelorder-motivo-${v}`}
                    key={v}
                    onClick={() => setStelOrderMotivo(v)}
                    className={`py-2 px-2 rounded-xl text-sm font-medium border transition-colors ${
                      stelOrderMotivo === v ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-300'
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Detalle {stelOrderMotivo === 'otro' ? '(obligatorio)' : '(opcional)'}
              </label>
              <textarea
                value={stelOrderDetalle}
                onChange={e => setStelOrderDetalle(e.target.value)}
                rows={3}
                placeholder="Qué tuviste que hacer en StelOrder..."
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div className="flex gap-3 pt-1">
              <button data-attr="stelorder-cancelar"
                onClick={() => { setShowStelOrderModal(false); setStelOrderMotivo(''); setStelOrderDetalle(''); }}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 font-semibold rounded-xl transition-colors text-sm"
              >
                Cancelar
              </button>
              <button data-attr="stelorder-enviar"
                onClick={handleEnviarStelOrder}
                disabled={!stelOrderMotivo || (stelOrderMotivo === 'otro' && !stelOrderDetalle.trim())}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Enviar
              </button>
            </div>
          </div>
        </div>
      )}

      {showContratoModal && (
        <NuevoContratoModal
          onClose={() => setShowContratoModal(false)}
          onSaved={() => setShowContratoModal(false)}
          clienteInicial={{
            clienteId:        String(order['account-id'] || ''),
            addressId:        String(order['work-address-id'] || ''),
            clienteNombre:    order.clientName || '',
            clienteTelefono:  order.clientPhone || '',
            clienteEmail:     '',
            municipio:        '',
            direccion:        order.addressText || '',
            marca:            (equipmentInfo || '').split(' ')[0] || '',
            modelo:           (equipmentInfo || '').split(' ').slice(1).join(' ') || '',
          }}
          captadorData={{
            tecnicoId:    String(user?.employeeId || ''),
            tecnicoNombre: user?.name || '',
            comision:     10,
            workorderRef: order['full-reference'] || String(order.id || ''),
          }}
          forzarLink
        />
      )}

      {/* El checklist/análisis/chat con IA están pensados para diagnosticar
          una avería — no hay avería que diagnosticar en una puesta en
          marcha (equipo recién instalado), así que no tiene sentido
          ofrecerlos ahí. */}
      {!esPuestaMarcha && (<>
      {/* ══════════════════════════════════════════════════════
          CHECKLIST GENERADO POR IA
      ══════════════════════════════════════════════════════ */}
      {!checklist ? (
        <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-4 space-y-3">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <p className="font-bold text-gray-800 text-sm">Checklist de revisión</p>
          </div>
          <p className="text-xs text-gray-400 leading-relaxed">
            Describe brevemente lo que estás viendo y la IA generará un checklist adaptado a este equipo y avería.
          </p>
          <textarea
            value={checklistDescription}
            onChange={e => setChecklistDescription(e.target.value)}
            placeholder="Ej: El equipo arranca pero no enfría, el compresor hace ruido al intentar arrancar y el ventilador de la unidad exterior gira a menor velocidad de lo normal..."
            rows={3}
            disabled={generatingChecklist}
            className="w-full text-sm text-gray-700 placeholder-gray-400 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent resize-none disabled:opacity-50"
          />
          {checklistError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-xs flex items-center gap-2">
              <span>⚠️</span>
              <span className="flex-1">{checklistError}</span>
              <button data-attr="checklist-ia-error-cerrar" onClick={() => setChecklistError('')} className="text-red-400 hover:text-red-600">✕</button>
            </div>
          )}
          <button data-attr="checklist-ia-generar"
            onClick={handleGenerateChecklist}
            disabled={!checklistDescription.trim() || generatingChecklist}
            className="w-full py-2.5 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors text-sm"
          >
            {generatingChecklist ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Generando checklist...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Generar checklist con IA
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm overflow-hidden">
          {/* Cabecera */}
          <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <p className="font-bold text-emerald-900 text-sm">Checklist IA</p>
              </div>
              <div className="mt-1.5 px-0.5">
                <ChecklistProgress items={checklist.items} />
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {checklist.items.every(i => !i.required || i.checked) && (
                <span className="flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  Completo
                </span>
              )}
              <button data-attr="checklist-ia-nuevo"
                onClick={() => setChecklist(null)}
                className="text-emerald-400 hover:text-emerald-600 text-xs underline"
              >
                Nuevo
              </button>
            </div>
          </div>

          {/* Ítems */}
          <div className="divide-y divide-gray-50">
            {checklist.items.map(item => (
              <button data-attr="checklist-ia-item"
                key={item.id}
                onClick={() => handleToggleItem(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  item.checked ? 'bg-emerald-50/50' : 'hover:bg-gray-50 active:bg-gray-100'
                }`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                  item.checked ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
                }`}>
                  {item.checked && (
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className={`flex-1 text-sm leading-snug ${item.checked ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                  {item.label}
                </span>
                {item.required && !item.checked && (
                  <span className="text-xs text-red-400 font-semibold flex-shrink-0">*</span>
                )}
              </button>
            ))}
          </div>

          {/* Descripción original del técnico */}
          {checklist.techDescription && (
            <div className="ph-no-capture px-4 py-3 bg-gray-50 border-t border-gray-100">
              <p className="text-xs text-gray-400 uppercase font-semibold tracking-wider mb-1">Observación del técnico</p>
              <p className="text-xs text-gray-500 leading-relaxed">{checklist.techDescription}</p>
            </div>
          )}
        </div>
      )}

      {/* ── Botón generar análisis ── */}
      {!analysis && (
        <button data-attr="analisis-ia-generar"
          onClick={handleAnalyze}
          disabled={analyzing}
          className="w-full py-3.5 flex items-center justify-center gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 disabled:opacity-60 text-white font-bold rounded-2xl transition-all text-sm shadow-sm"
        >
          {analyzing ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Analizando con IA...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Generar análisis IA
            </>
          )}
        </button>
      )}

      {aiError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm flex items-center gap-2">
          <span>⚠️</span>
          <span className="flex-1">{aiError}</span>
          <button data-attr="analisis-ia-error-cerrar" onClick={() => setAiError('')} className="text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* ── Resultado del análisis IA ── */}
      {analysis && (
        <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-2xl p-4 border border-violet-100 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center">
                <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <p className="font-bold text-violet-900 text-sm leading-tight">Análisis IA</p>
                {usedContext > 0 && (
                  <p className="text-xs text-violet-500 leading-tight">
                    Basado en {usedContext} caso{usedContext !== 1 ? 's' : ''} anterior{usedContext !== 1 ? 'es' : ''} de tu equipo
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {analysis.estimatedTime && (
                <span className="text-xs font-semibold text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full">
                  ⏱ {analysis.estimatedTime}
                </span>
              )}
              <button data-attr="analisis-ia-regenerar"
                onClick={() => { setAnalysis(null); setAiError(''); setThreadId(null); setChatMessages([]); setFeedbackRating(null); setFeedbackText(''); setFeedbackSaved(false); setUsedContext(0); }}
                className="text-violet-400 hover:text-violet-600 text-xs underline"
              >
                Regenerar
              </button>
            </div>
          </div>

          {analysis.solution && (
            <div>
              <p className="text-xs font-bold text-violet-700 uppercase tracking-wider mb-1.5">Diagnóstico</p>
              <p className="text-sm text-gray-800 leading-relaxed">{analysis.solution}</p>
            </div>
          )}

          <StepsList steps={analysis.steps} />
          <ListSection title="Materiales" items={analysis.materials} />
          <ListSection title="Herramientas" items={analysis.tools} />
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          CHAT CON LA IA
      ══════════════════════════════════════════════════════ */}
      {analysis && (
        <div className="rounded-2xl border border-violet-100 overflow-hidden">

          {/* Cabecera del chat */}
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-3 flex items-center gap-2">
            <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
            <p className="text-white font-bold text-sm">Chat con IA</p>
            <p className="text-violet-200 text-xs ml-auto">Pregunta sobre esta avería</p>
          </div>

          {/* Mensajes */}
          <div className="ph-no-capture bg-gray-50 px-3 py-3 space-y-3 min-h-[80px] max-h-[340px] overflow-y-auto">
            {chatMessages.length === 0 && !chatLoading && (
              <p className="text-center text-xs text-gray-400 py-4">
                Pregúntame cualquier duda sobre el diagnóstico o la reparación
              </p>
            )}
            {chatMessages.map((msg, i) => (
              <ChatBubble key={i} msg={msg} />
            ))}
            {chatLoading && (
              <div className="flex items-end gap-2">
                <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div className="bg-white border border-violet-100 rounded-2xl rounded-bl-sm px-4 py-2.5 shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatBottomRef} />
          </div>

          {/* Aviso de conversación larga */}
          {chatMessages.length >= 12 && (
            <div className="bg-amber-50 border-t border-amber-200 px-3 py-2 flex items-center justify-between gap-2">
              <span className="text-xs text-amber-700">Conversación larga — el coste aumenta con cada mensaje</span>
              <button data-attr="chat-ia-nueva"
                onClick={handleResetChat}
                className="text-xs font-semibold text-amber-800 hover:text-amber-900 underline flex-shrink-0"
              >
                Nueva conversación
              </button>
            </div>
          )}

          {/* Input del chat */}
          <div className="bg-white border-t border-gray-100 p-2 flex items-end gap-2">
            <textarea
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder="Escribe tu pregunta..."
              rows={1}
              disabled={chatLoading}
              className="flex-1 resize-none text-sm text-gray-800 placeholder-gray-400 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-transparent disabled:opacity-50 max-h-24 overflow-y-auto"
            />
            <button data-attr="chat-ia-enviar"
              onClick={handleSendMessage}
              disabled={!chatInput.trim() || chatLoading}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-violet-600 hover:bg-violet-700 disabled:bg-gray-200 text-white rounded-xl transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          FEEDBACK DEL TÉCNICO (para aprendizaje de la IA)
      ══════════════════════════════════════════════════════ */}
      {analysis && !feedbackSaved && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">¿Fue útil el diagnóstico?</p>
          <p className="text-xs text-gray-400">Tu respuesta ayuda a la IA a mejorar para casos futuros</p>

          <div className="flex gap-2">
            <button data-attr="analisis-ia-feedback-si"
              onClick={() => setFeedbackRating('up')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
                feedbackRating === 'up'
                  ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-300'
                  : 'bg-gray-50 text-gray-600 hover:bg-emerald-50 hover:text-emerald-600'
              }`}
            >
              👍 Sí, correcto
            </button>
            <button data-attr="analisis-ia-feedback-no"
              onClick={() => setFeedbackRating('down')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
                feedbackRating === 'down'
                  ? 'bg-red-100 text-red-700 ring-2 ring-red-300'
                  : 'bg-gray-50 text-gray-600 hover:bg-red-50 hover:text-red-600'
              }`}
            >
              👎 No era eso
            </button>
          </div>

          {feedbackRating !== null && (
            <div className="space-y-2">
              <textarea
                value={feedbackText}
                onChange={e => setFeedbackText(e.target.value)}
                placeholder={feedbackRating === 'down'
                  ? '¿Qué encontraste realmente? (opcional, pero muy útil para el equipo)'
                  : '¿Algo que añadir? (opcional)'}
                rows={2}
                className="w-full text-sm text-gray-700 placeholder-gray-400 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-transparent resize-none"
              />
              <button data-attr="analisis-ia-feedback-enviar"
                onClick={handleSaveFeedback}
                disabled={feedbackSaving}
                className="w-full py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white font-semibold rounded-xl text-sm transition-colors flex items-center justify-center gap-2"
              >
                {feedbackSaving ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Guardando...
                  </>
                ) : (
                  'Enviar feedback'
                )}
              </button>
              {feedbackError && <p className="text-xs text-red-600">{feedbackError}</p>}
            </div>
          )}
        </div>
      )}

      {/* Confirmación de feedback guardado */}
      {feedbackSaved && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-3 text-sm font-medium">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Feedback guardado. ¡Gracias! La IA aprenderá de tu experiencia.
        </div>
      )}
      </>)}

      {/* Pendiente: todavía no iniciada — el botón sticky "Iniciar visita" la
          mueve a En ruta para poder finalizarla después. */}
      {showIniciarButton && (
        <div className="flex items-start gap-3 py-3 px-4 bg-blue-50 border border-blue-200 rounded-2xl text-sm text-blue-800">
          <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Todavía no has iniciado esta visita. Pulsa "Iniciar visita" para empezar.</span>
        </div>
      )}

      {/* Badge de estado final cuando la orden ya está cerrada/finalizada */}
      {stateId !== 651457 && stateId !== 1018827 && stateId !== 1004686 && stateId !== 1305009 && (
        <div className="flex items-center justify-center gap-2 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm text-gray-500">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Visita registrada como <strong className="text-gray-700 ml-1">{STATE_LABEL[stateId] || 'Cerrada'}</strong>
        </div>
      )}

      {/* Modal finalizar visita */}
      {showFinalizarModal && (
        <FinalizarVisitaModal
          order={localOrder}
          onClose={() => setShowFinalizarModal(false)}
          onSuccess={handleFinalizarSuccess}
        />
      )}

    </div>

    {/* ── Botón sticky Iniciar visita (Pendiente → En ruta) ── */}
    {showIniciarButton && (
      <div className="fixed bottom-20 left-0 right-0 p-4 bg-white/95 backdrop-blur-sm border-t border-gray-100 shadow-xl z-30">
        {iniciarError && (
          <div className="mb-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-xs flex items-center gap-2">
            <span className="flex-1">{iniciarError}</span>
            <button data-attr="visita-iniciar-error-cerrar" onClick={() => setIniciarError('')} className="text-red-400 hover:text-red-600">✕</button>
          </div>
        )}
        <button data-attr="visita-iniciar"
          onClick={handleIniciarVisita}
          disabled={startingVisit}
          className="w-full flex items-center justify-center gap-2.5 py-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60 text-white font-bold rounded-2xl text-base transition-all"
        >
          {startingVisit ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {startingVisit ? 'Iniciando...' : 'Iniciar visita'}
        </button>
      </div>
    )}

    {/* ── Botón sticky Finalizar visita ── */}
    {showStickyButton && (
      <div className="fixed bottom-20 left-0 right-0 p-4 bg-white/95 backdrop-blur-sm border-t border-gray-100 shadow-xl z-30">
        <button data-attr="visita-finalizar"
          onClick={() => setShowFinalizarModal(true)}
          className="w-full flex items-center justify-center gap-2.5 py-4 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-bold rounded-2xl text-base transition-all"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {esPuestaMarcha ? 'Iniciar puesta en marcha' : 'Finalizar visita'}
        </button>
      </div>
    )}

    {/* ── Botón sticky Corregir visita (ventana corta tras finalizar) ── */}
    {showCorregirButton && (
      <div className="fixed bottom-20 left-0 right-0 p-4 bg-white/95 backdrop-blur-sm border-t border-gray-100 shadow-xl z-30">
        <button data-attr="visita-corregir"
          onClick={() => setShowFinalizarModal(true)}
          className="w-full flex items-center justify-center gap-2.5 py-3.5 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-bold rounded-2xl text-sm transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          Corregir visita
        </button>
      </div>
    )}
    </>
  );
}
